// src/pages/calendar/bookings/utils/bookingUtils.js
import dayjs from "dayjs";

/**
 * Determine booking status based on booking details and event type
 * @param {Object} booking - The booking object
 * @param {Object} eventType - The event type object
 * @returns {string} - Status: upcoming, past, pending, canceled, etc.
 */
export const getBookingStatus = (booking, eventType) => {
  // If booking already has a status field, prioritize it
  if (booking.status) {
    return booking.status;
  }

  // If event type requires confirmation
  if (eventType?.advanced?.requiresConfirmation?.enabled) {
    return "pending"; // Default status for bookings requiring confirmation
  }

  // Parse booking date and time
  const bookingDate = dayjs(booking.date);
  const timeStart = booking.timeStart;

  // Combine date and time start
  let bookingDateTime;
  try {
    // Handle different time formats (12h vs 24h)
    if (
      timeStart.toLowerCase().includes("am") ||
      timeStart.toLowerCase().includes("pm")
    ) {
      // 12-hour format: "10:30am"
      const timeStr = timeStart.toLowerCase();
      const isPM = timeStr.includes("pm");

      // Extract hours and minutes
      let [hourMin] = timeStr.split(/am|pm/i);
      let [hours, minutes] = hourMin
        .split(":")
        .map((num) => parseInt(num.trim(), 10));

      // Adjust for PM if needed (except for 12 PM)
      if (isPM && hours !== 12) {
        hours += 12;
      }
      // Adjust for 12 AM
      if (!isPM && hours === 12) {
        hours = 0;
      }

      bookingDateTime = bookingDate.hour(hours).minute(minutes || 0);
    } else {
      // 24-hour format: "14:30"
      const [hours, minutes] = timeStart.split(":").map(Number);
      bookingDateTime = bookingDate.hour(hours).minute(minutes || 0);
    }
  } catch (error) {
    console.error("Error parsing booking time:", error);
    bookingDateTime = bookingDate; // Fallback to just the date
  }

  // Compare with current date and time
  const now = dayjs();

  if (bookingDateTime.isBefore(now)) {
    return "past";
  }

  return "confirmed"; // Changed from "upcoming" to "confirmed" for clearer status
};

/**
 * Format date and time for display
 * @param {string} date - Date string in YYYY-MM-DD format
 * @param {string} timeStart - Start time string
 * @param {string} timeEnd - End time string
 * @param {string} userTimezone - User's timezone
 * @returns {object} - Formatted date and time strings
 */
export const formatDateTimeDisplay = (
  date,
  timeStart,
  timeEnd,
  userTimezone
) => {
  if (!date) return { dateDisplay: "N/A", timeDisplay: "N/A" };

  const bookingDate = dayjs(date);

  // Format date as "Sun, 30 Mar 2025"
  const dateDisplay = bookingDate.format("ddd, D MMM YYYY");

  // Format time as "10:30 AM - 11:00 AM"
  let timeDisplay = `${timeStart} - ${timeEnd}`;

  // Add timezone if available
  if (userTimezone) {
    timeDisplay += ` (${userTimezone})`;
  }

  return { dateDisplay, timeDisplay };
};

/**
 * Check if a date is on a weekend
 * @param {Date|string} date - Date to check
 * @returns {boolean} - True if weekend
 */
export const isWeekend = (date) => {
  const day = dayjs(date).day();
  return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
};

/**
 * Get business days between two dates
 * @param {Date|string} startDate - Start date
 * @param {Date|string} endDate - End date
 * @returns {number} - Number of business days
 */
export const getBusinessDaysBetween = (startDate, endDate) => {
  let count = 0;
  let current = dayjs(startDate);
  const end = dayjs(endDate);

  while (current.isBefore(end)) {
    if (!isWeekend(current)) {
      count++;
    }
    current = current.add(1, "day");
  }

  return count;
};

/**
 * Get a color for booking status chip
 * @param {string} status - Booking status
 * @returns {string} - Material UI color
 */
export const getStatusColor = (status) => {
  // Make sure to provide fallback values to avoid undefined errors
  switch (status) {
    case "upcoming":
    case "confirmed":
      return "success";
    case "pending":
      return "warning";
    case "canceled":
    case "rejected":
      return "error";
    case "past":
      return "default";
    default:
      return "default";
  }
};

/**
 * Get a display label for booking status
 * @param {string} status - Booking status
 * @returns {string} - Display label
 */
export const getStatusLabel = (status) => {
  switch (status) {
    case "upcoming":
      return "Upcoming";
    case "confirmed":
      return "Confirmed";
    case "pending":
      return "Unconfirmed";
    case "canceled":
      return "Canceled";
    case "rejected":
      return "Rejected";
    case "past":
      return "Past";
    default:
      return "Unknown";
  }
};
